import { findTransportLocation } from "@elit-hair/elitklinik-shared";
import { tooltipClasses } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { motion } from "motion/react";
import { useMemo } from "react";
import { TbAlertTriangle } from "react-icons/tb";

import { formatFlightNumber } from "@/helpers/strings";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import { IPlanPassenger } from "@/types/tasks.types";

type WarningRule = {
    id: string;
    check: (row: IPlanPassenger<"api">, index: number) => boolean;
    comp: (props: { row: IPlanPassenger<"api">; index: number }) => JSX.Element;
};

// PC - IST Uyarı Bileşeni
function PcIstWarning({ row }: { row: IPlanPassenger<"api">; index: number }) {
    return (
        <span>
            Yolcu uçak kodu <span className="font-semibold">{formatFlightNumber(row.flightNumber || "")}</span> ama
            havalimanı
            <span className="font-semibold"> IST</span> olarak kaydedilmiş
        </span>
    );
}

// Uyarı kuralları - yeni kural eklemek için buraya ekle
const warningRules: WarningRule[] = [
    {
        id: "pc-ist-mismatch",
        check: (row) => {
            const flightNumber = row.flightNumber || "";
            const airportValue = (row.isFlightArrival ? row.origin?.alias : row.destination?.alias) || "";

            // If flight number starts with PC and airport is IST, return true
            if (typeof flightNumber === "string" && flightNumber.trim().toUpperCase().startsWith("PC")) {
                const airport = findTransportLocation(airportValue);
                return !!(airport && airport.label === "IST");
            }

            return false;
        },
        comp: PcIstWarning,
    },
];

function WarningTooltipHeader({ count }: { count: number }) {
    return (
        <div className="flex items-center gap-2 pb-2 border-b border-red-100">
            <TbAlertTriangle className="w-4 h-4 text-red-600" />
            <span className="font-semibold text-red-900">Yolcu Uyarıları ({count})</span>
        </div>
    );
}

function WarningTooltipList({
    warnings,
    row,
    index,
}: {
    warnings: WarningRule[];
    row: IPlanPassenger<"api">;
    index: number;
}) {
    return (
        <ul className="space-y-2 list-none">
            {warnings.map((warning) => (
                <li key={warning.id} className="flex items-start gap-2 text-gray-700">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span className="flex-1">{warning.comp({ row, index })}</span>
                </li>
            ))}
        </ul>
    );
}

function WarningTooltipContent({
    warnings,
    row,
    index,
}: {
    warnings: WarningRule[];
    row: IPlanPassenger<"api">;
    index: number;
}) {
    return (
        <div className="space-y-2">
            <WarningTooltipHeader count={warnings.length} />
            <WarningTooltipList warnings={warnings} row={row} index={index} />
        </div>
    );
}

function WarningBadge({ count }: { count: number }) {
    if (count <= 1) return null;

    return (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {count}
        </span>
    );
}

function WarningIcon({ warnings }: { warnings: WarningRule[] }) {
    return (
        <motion.div
            animate={{
                scale: [1, 1.15, 1],
                rotate: [0, 5, -5, 0],
            }}
            transition={{
                duration: 2.5,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
            }}
            className="relative flex items-center justify-center w-7 h-7 bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200"
        >
            <TbAlertTriangle className="w-4 h-4 text-red-600" />
            <WarningBadge count={warnings.length} />
        </motion.div>
    );
}

function PlanRowWarning({ row, index }: { row: IPlanPassenger<"api">; index: number }) {
    const warnings = useMemo(() => warningRules.filter((rule) => rule.check(row, index)), [row, index]);
    const hasWarnings = warnings.length > 0;

    return (
        hasWarnings && (
            <NoMaxWidthTooltip
                arrow
                sx={{
                    [`& .${tooltipClasses.tooltip}`]: {
                        backgroundColor: "white",
                        border: "1px solid #fee2e2",
                        padding: "12px !important",
                        fontSize: 12,
                        maxWidth: "320px !important",
                        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    },
                }}
                title={<WarningTooltipContent warnings={warnings} row={row} index={index} />}
            >
                <span className="inline-block cursor-help absolute">
                    <WarningIcon warnings={warnings} />
                </span>
            </NoMaxWidthTooltip>
        )
    );
}

function PlanOrderColumn(): GridColDef<IPlanPassenger<"api">> {
    return {
        field: "index",
        sortable: false,
        filterable: false,
        headerName: "",
        width: 35,
        minWidth: 35,
        maxWidth: 35,
        renderCell: ({ api, row }) => {
            const order = api.getRowIndexRelativeToVisibleRows(row.id);

            return (
                <div className="relative flex items-center justify-center h-full font-medium">
                    <PlanRowWarning row={row} index={order} />
                    {order >= 0 ? order + 1 : ""}
                </div>
            );
        },
    };
}

export default PlanOrderColumn;
